import React, { useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Slider,
  TextField,
  Stack,
  Chip,
  FormControlLabel,
  Switch,
  Paper,
  Collapse,
  IconButton,
} from '@mui/material'
import { ExpandMore as ExpandIcon, ExpandLess as CollapseIcon } from '@mui/icons-material'
import {
  RATING_FACTOR_META,
  calculateLocalOverall,
  getScoreLabel,
} from '../../constants/ratingFactors'
import { RatingInput } from '@store/api/ratingApi'

export interface MultiFactorRatingValue extends RatingInput {
  flavor: number
  construction: number
  burn: number
  value: number
}

interface MultiFactorRatingFormProps {
  value: MultiFactorRatingValue
  onChange: (value: MultiFactorRatingValue) => void
  disabled?: boolean
  weights?: { flavor: number; construction: number; burn: number; value: number }
}

const SLIDER_MARKS = [1, 3, 5, 7, 10].map((v) => ({ value: v, label: String(v) }))

export function isCompleteRating(value: MultiFactorRatingValue): boolean {
  return [value.flavor, value.construction, value.burn, value.value].every(
    (score) => Number.isInteger(score) && score >= 1 && score <= 10
  )
}

const MultiFactorRatingForm: React.FC<MultiFactorRatingFormProps> = ({
  value,
  onChange,
  disabled = false,
  weights,
}) => {
  const [expandedNotes, setExpandedNotes] = useState<Record<string, boolean>>({})

  const calculatedOverall = useMemo(() => {
    if (!isCompleteRating(value)) return null
    if (value.isOverallManual && value.overall) return value.overall
    return calculateLocalOverall(
      { flavor: value.flavor, construction: value.construction, burn: value.burn, value: value.value },
      weights
    )
  }, [value, weights])

  const updateFactor = (key: keyof MultiFactorRatingValue, factorValue: number | string | boolean) => {
    onChange({ ...value, [key]: factorValue })
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom fontWeight={600}>
        Multi-Factor Rating (1–10)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Rate flavor, construction, burn, and value. Overall is calculated automatically unless you override it.
      </Typography>

      <Stack spacing={3}>
        {RATING_FACTOR_META.map((factor) => {
          const score = value[factor.key as keyof MultiFactorRatingValue] as number
          const notesExpanded = expandedNotes[factor.key] ?? false
          return (
            <Box key={factor.key}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    {factor.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {factor.description}
                  </Typography>
                </Box>
                {score >= 1 && (
                  <Chip
                    size="small"
                    label={getScoreLabel(score, factor.lowLabel, factor.highLabel)}
                    color={score >= 8 ? 'success' : score <= 3 ? 'warning' : 'default'}
                  />
                )}
              </Box>
              <Slider
                value={score || 1}
                min={1}
                max={10}
                step={1}
                marks={SLIDER_MARKS}
                valueLabelDisplay="on"
                disabled={disabled}
                sx={{ py: 2, '& .MuiSlider-thumb': { width: 28, height: 28 } }}
                onChange={(_, newValue) => updateFactor(factor.key as keyof MultiFactorRatingValue, newValue as number)}
              />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  {factor.lowLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {factor.highLabel}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <IconButton
                  size="small"
                  onClick={() => setExpandedNotes((prev) => ({ ...prev, [factor.key]: !notesExpanded }))}
                  aria-label={`${notesExpanded ? 'Hide' : 'Show'} ${factor.label} notes`}
                >
                  {notesExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                </IconButton>
                <Typography variant="caption" color="text.secondary">
                  Add notes
                </Typography>
              </Box>
              <Collapse in={notesExpanded}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  placeholder={`Notes on ${factor.label.toLowerCase()}...`}
                  value={(value[factor.notesKey] as string) || ''}
                  disabled={disabled}
                  onChange={(e) => updateFactor(factor.notesKey as keyof MultiFactorRatingValue, e.target.value)}
                  sx={{ mt: 1 }}
                />
              </Collapse>
            </Box>
          )
        })}

        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(value.isOverallManual)}
                onChange={(e) => updateFactor('isOverallManual', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Set overall score manually"
          />
          {value.isOverallManual ? (
            <Slider
              value={value.overall || calculatedOverall || 1}
              min={1}
              max={10}
              step={1}
              marks={SLIDER_MARKS}
              valueLabelDisplay="on"
              disabled={disabled}
              sx={{ py: 2, '& .MuiSlider-thumb': { width: 28, height: 28 } }}
              onChange={(_, newValue) => updateFactor('overall', newValue as number)}
            />
          ) : (
            <Typography variant="h5" color="primary" fontWeight={700}>
              Overall: {calculatedOverall ?? '—'}/10
            </Typography>
          )}
          <Collapse in={Boolean(value.isOverallManual || value.overallNotes)}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Overall notes"
              value={value.overallNotes || ''}
              disabled={disabled}
              onChange={(e) => updateFactor('overallNotes', e.target.value)}
              sx={{ mt: 1 }}
            />
          </Collapse>
        </Box>
      </Stack>
    </Paper>
  )
}

export default MultiFactorRatingForm
